// components/ThreeDotMenu.tsx
import React, { useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import PaletteIcon from "@mui/icons-material/Palette";
import AnimationIcon from "@mui/icons-material/Animation";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import { CustomPhotoAvatar } from "../../../../context/types";

interface ThreeDotMenuProps {
  avatar: CustomPhotoAvatar;
  onDelete: (avatarId: string) => void;
  onOpenGroupCreation: (avatar: CustomPhotoAvatar) => void;
  canCreateGroup: boolean;
  isCreatingGroup?: boolean;
  onOpenAddMotion?: (avatar: CustomPhotoAvatar) => void;
  onAddSoundEffect?: (avatar: CustomPhotoAvatar) => void;
  isAddingMotion?: boolean;
  isAddingSoundEffect?: boolean;
  // V3: rename look — only present for non-prompt V3 avatars
  onRename?: () => void;
}

const ThreeDotMenu: React.FC<ThreeDotMenuProps> = ({
  avatar,
  onDelete,
  onOpenGroupCreation,
  canCreateGroup,
  isCreatingGroup = false,
  onOpenAddMotion,
  onAddSoundEffect,
  isAddingMotion = false,
  isAddingSoundEffect = false,
  onRename,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Check if avatar can have motion added (must be successful and not already have motion)
  const canAddMotion = avatar.status === "success" && !avatar.is_motion;

  // Check if avatar can have sound effect added (must have motion first and not already have sound effect)
  const canAddSoundEffect = avatar.status === "success" && avatar.is_motion === true && !avatar.background_sound_effect;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    // COMPLETELY stop the event from reaching the parent
    event.stopPropagation();
    event.preventDefault();
    event.nativeEvent.stopImmediatePropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleCreateGroupClick = () => {
    onOpenGroupCreation(avatar);
    handleMenuClose();
  };

  const handleAddMotionClick = () => {
    onOpenAddMotion?.(avatar);
    handleMenuClose();
  };

  const handleAddSoundEffectClick = () => {
    onAddSoundEffect?.(avatar);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    onDelete(avatar._id);
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <div
      onClick={(e) => {
        // This div wrapper stops all clicks from bubbling
        e.stopPropagation();
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
      }}
      onMouseDown={(e) => {
        // Also stop mousedown events
        e.stopPropagation();
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
      }}
      style={{ display: "inline-block" }}
    >
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          zIndex: 10,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 1)",
          },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.nativeEvent.stopImmediatePropagation();
        }}
      >
        {canCreateGroup && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              handleCreateGroupClick();
            }}
            disabled={isCreatingGroup}
          >
            <ListItemIcon>
              <PaletteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {isCreatingGroup ? "Creating Group..." : "Add Avatar Looks"}
            </ListItemText>
          </MenuItem>
        )}
        {canAddMotion && onOpenAddMotion && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              handleAddMotionClick();
            }}
            disabled={isAddingMotion}
          >
            <ListItemIcon>
              <AnimationIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {isAddingMotion ? "Adding Motion..." : "Add Motion"}
            </ListItemText>
          </MenuItem>
        )}
        {canAddSoundEffect && onAddSoundEffect && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              handleAddSoundEffectClick();
            }}
            disabled={isAddingSoundEffect}
          >
            <ListItemIcon>
              <MusicNoteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {isAddingSoundEffect ? "Adding Sound Effect..." : "Add Sound Effect"}
            </ListItemText>
          </MenuItem>
        )}
        {onRename && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              onRename();
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <PaletteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rename</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
            handleDeleteClick();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={(e: any) => {
          e?.stopPropagation();
          e?.preventDefault();
          handleDeleteCancel();
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.nativeEvent.stopImmediatePropagation();
        }}
      >
        <DialogTitle>Delete Avatar</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{avatar.name}"? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              handleDeleteCancel();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              handleDeleteConfirm();
            }}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ThreeDotMenu;
